import { CategoryOption } from "../store/service-logic-builder-slice";
import {
  isMeasurementUnitParameter,
  formatHeightDisplay,
  convertWeightToDisplay,
  getWeightUnit,
} from "../components/logicBuilder/measurementUnitHelpers";
import { formatDisplayDate } from "../../../common/utils/date";

/**
 * Rule Formatting Utilities
 *
 * These functions are used to convert technical field names and values
 * into user-friendly display labels for rules/conditions in logic builder
 * and reports.
 */

// Display name to server key mapping
export const CATEGORY_SERVER_MAPPING = {
  "Biometric Questionnaire": "biometric_questionnaire",
  "Lifestyle Questionnaire": "lifestyle_questionnaire",
  "Nutrition Questionnaire": "nutrition_questionnaire",
  "Pnoe Metrics": "pnoe_metrics",
  "Lab Work": "lab_work",
  "Wearable Integration": "wearable_integration",
} as const;

/**
 * Convert server key to display name
 * E.g., "biometric_questionnaire" -> "Biometric Questionnaire"
 */
export const getDisplayCategoryName = (serverKey: string): string => {
  const entry = Object.entries(CATEGORY_SERVER_MAPPING).find(
    ([_, value]) => value === serverKey
  );
  return entry
    ? entry[0]
    : serverKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Convert display name to server key
 * E.g., "Biometric Questionnaire" -> "biometric_questionnaire"
 */
export const getServerCategoryKey = (displayName: string): string => {
  return (
    CATEGORY_SERVER_MAPPING[
      displayName as keyof typeof CATEGORY_SERVER_MAPPING
    ] || displayName.toLowerCase().replace(/\s+/g, "_")
  );
};

export enum OperatorType {
  LESS_THAN = "<",
  LESS_THAN_OR_EQUAL = "<=",
  EQUALS = "=",
  GREATER_THAN_OR_EQUAL = ">=",
  GREATER_THAN = ">",
  EXCLUDES = "!==",
  INCLUDES = "contains",
}

// Mapping from operator symbols to display text
export const OPERATOR_DISPLAY_MAPPING: Record<OperatorType, string> = {
  [OperatorType.EQUALS]: "Equals",
  [OperatorType.GREATER_THAN]: "Greater than",
  [OperatorType.GREATER_THAN_OR_EQUAL]: "Greater than or equal to",
  [OperatorType.LESS_THAN]: "Less than",
  [OperatorType.LESS_THAN_OR_EQUAL]: "Less than or equal to",
  [OperatorType.EXCLUDES]: "Excludes",
  [OperatorType.INCLUDES]: "Includes",
};

/**
 * Get display text from operator value
 * E.g., "=" -> "Equals", ">=" -> "Greater than or equal to"
 */
export const getOperatorDisplayText = (operator: string): string => {
  return (
    OPERATOR_DISPLAY_MAPPING[operator as OperatorType] || operator || ""
  );
};

/**
 * Get operator value from display text
 * E.g., "Equals" -> "="
 */
export const getOperatorFromDisplayText = (
  displayText: string
): OperatorType | null => {
  const entry = Object.entries(OPERATOR_DISPLAY_MAPPING).find(
    ([_, value]) => value === displayText
  );
  return entry ? (entry[0] as OperatorType) : null;
};

/**
 * Capitalize first letter of a string
 * E.g., "all" -> "All"
 */
export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get field option by ID from category options
 *
 * @param categoryOptions - The category options from Redux store
 * @param objectName - The server key of the category (e.g., "biometric_questionnaire")
 * @param fieldId - The field ID to look up
 * @returns The field option or null if not found
 */
export const getFieldOptionById = (
  categoryOptions: Record<string, CategoryOption[]>,
  objectName: string | null,
  fieldId: string
): CategoryOption | null => {
  if (!objectName) return null;

  // Convert server key to display name for lookup
  const displayCategoryName = getDisplayCategoryName(objectName);

  if (!categoryOptions[displayCategoryName]) return null;
  return (
    categoryOptions[displayCategoryName].find(
      (option: CategoryOption) => option.id === fieldId
    ) || null
  );
};

/**
 * Get user-friendly display label for a field
 * Falls back to converting snake_case to Title Case if not found
 *
 * @param categoryOptions - The category options from Redux store
 * @param objectName - The server key of the category
 * @param fieldId - The field ID to look up
 * @returns User-friendly field label
 */
export const getFieldDisplayLabel = (
  categoryOptions: Record<string, CategoryOption[]>,
  objectName: string | null,
  fieldId: string
): string => {
  const fieldOption = getFieldOptionById(categoryOptions, objectName, fieldId);
  if (fieldOption?.label) {
    return fieldOption.label;
  }

  // Safety check for undefined fieldId
  if (!fieldId) {
    return "Unknown Field";
  }

  // Fallback: convert technical field name to display name
  // Convert snake_case to Title Case
  return fieldId
    .split("_")
    .filter((word) => word.length > 0) // Filter out empty strings
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Get choice label by index or id
 * Converts choice index/id to user-friendly label
 *
 * @param fieldOption - The field option containing choices
 * @param value - The value to look up (can be index, id, or array)
 * @returns The choice label or the raw value if not found
 */
export const getChoiceLabelByIndex = (
  fieldOption: CategoryOption | null,
  value: string | number | string[] | number[]
): string => {
  // If no field option or no choices, return the raw value
  if (
    !fieldOption ||
    !fieldOption.choices ||
    fieldOption.choices.length === 0
  ) {
    return value.toString();
  }

  // Find the choice by index or id (supporting both structures)
  const choice = fieldOption.choices.find((choice) => {
    // Handle index-based structure (current)
    if (choice.index !== undefined) {
      return choice.index.toString() === value.toString();
    }
    // Handle id-based structure (array or single number)
    if (choice.id !== undefined) {
      if (Array.isArray(choice.id)) {
        // Choice has array id, value might be comma-separated string
        const valueAsArray =
          typeof value === "string" && value.includes(",")
            ? value
                .split(",")
                .map((v) => Number(v.trim()))
                .sort()
            : [Number(value)];
        return (
          JSON.stringify([...choice.id].sort()) ===
          JSON.stringify(valueAsArray)
        );
      } else {
        return choice.id.toString() === value.toString();
      }
    }
    return false;
  });

  // Return label or fallback to raw value
  return choice ? choice.label : value.toString();
};

/**
 * Get formatted display value for a field
 * Handles both choice-based fields (questionnaires) and numeric fields (metrics)
 *
 * @param categoryOptions - The category options from Redux store
 * @param objectName - The server key of the category
 * @param fieldId - The field ID
 * @param value - The field value
 * @param unitType - Optional unit type (1 = metric, 2 = imperial) for measurement conversions
 * @returns Formatted display value with units or choice label
 */
export const getFieldDisplayValue = (
  categoryOptions: Record<string, CategoryOption[]>,
  objectName: string | null,
  fieldId: string,
  value: string | number | string[] | number[] | null,
  unitType?: number
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  const fieldOption = getFieldOptionById(categoryOptions, objectName, fieldId);
  let fieldUnit = fieldOption?.unit || "";

  // For biometric/lifestyle questions with choices, show the choice label
  if (fieldOption?.choices && fieldOption.choices.length > 0) {
    return getChoiceLabelByIndex(fieldOption, value);
  }

  // Date criteria (e.g. birth_date) — format via shared util
  if (fieldOption?.type === "datepicker") {
    return formatDisplayDate(value as string);
  }

  // Handle measurement unit conversion if unitType is provided
  let valueToDisplay: string | number | string[] | number[] = value;

  if (unitType && isMeasurementUnitParameter(fieldId)) {
    const numericValue = Number(value);

    if (fieldId === "height") {
      // Format height display based on unitType
      return formatHeightDisplay(numericValue, unitType);
    } else if (fieldId === "current_weight" || fieldId === "target_weight") {
      if (unitType === 2) {
        // Convert kg to lbs (rounded)
        valueToDisplay = convertWeightToDisplay(numericValue, unitType);
        fieldUnit = getWeightUnit(unitType);
      } else {
        // Metric - keep as kg with decimals
        valueToDisplay = numericValue;
        fieldUnit = getWeightUnit(unitType);
      }
    }
  }

  // For numeric fields (like Pnoe Metrics), show value with unit
  const shouldShowUnit = fieldUnit && fieldUnit !== "high/low";
  if (shouldShowUnit) {
    return `${valueToDisplay}${fieldUnit !== "%" ? " " : ""}${fieldUnit}`;
  }

  // Handle High/Low special values
  if (valueToDisplay === 100) return "High";
  if (valueToDisplay === 10) return "Low";

  return valueToDisplay.toString();
};
